import { Box, Button, Divider, Menu, MenuItem, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  PRESENCE_STATUS_LABELS,
  PRESENCE_STATUSES,
  ROLE_LABELS,
  type SessionUser,
} from "@xop/dashboard-shared";
import { useId, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider.js";
import { useCan } from "../auth/permissions.js";
import { useRealtime } from "../lib/notifications/RealtimeProvider.js";
import { PresenceDot } from "./PresenceDot.js";
import { UserAvatar } from "./UserAvatar.js";

/**
 * Avatar-trigger dropdown for the top-right of the app shell. MUI `Menu`
 * provides click-outside / Escape / focus management and ARIA
 * (`role="menu"`, `menuitem`); the trigger carries `aria-haspopup` /
 * `aria-expanded` / `aria-controls`.
 */
export function ProfileMenu({ user }: { user: SessionUser }): JSX.Element {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const canSeeSystemAdmin = useCan("admin:users");
  const { status, setStatus } = useRealtime();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const menuId = useId();
  const close = () => setAnchorEl(null);

  const onSignOut = async () => {
    close();
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <>
      <Button
        type="button"
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open}
        // Surface the live availability on the trigger itself: a tooltip for
        // humans + `data-status` so it's observable without opening the menu
        // (opening the menu would itself count as activity and clear auto-away).
        data-testid="profile-menu-trigger"
        data-status={status}
        title={`${user.name} — ${PRESENCE_STATUS_LABELS[status]}`}
        {...(open ? { "aria-controls": menuId } : {})}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{ fontSize: 14, color: "text.disabled", transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms" }}
          />
        }
        sx={{
          borderRadius: 999,
          pl: 0.5,
          pr: 1,
          py: 0.5,
          color: "text.primary",
          textTransform: "none",
          fontWeight: 500,
          "&:hover": { bgcolor: "action.hover" },
          ...(open ? { bgcolor: "action.hover" } : {}),
        }}
      >
        {/* The user's own avatar — the SAME shared <UserAvatar> teammates see
            in chat + the presence rail, with a live status dot. */}
        <UserAvatar name={user.name} userId={user.id} size={34} presence={status} />
        <Box component="span" sx={{ display: { xs: "none", md: "inline" }, ml: 1, whiteSpace: "nowrap", fontSize: 14 }}>
          {user.name}
        </Box>
      </Button>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 256, mt: 1 } } }}
        MenuListProps={{ "aria-label": "Account menu" }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>{user.name}</Typography>
          <Typography sx={{ mt: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12, color: "text.disabled" }}>
            {user.email}
          </Typography>
          <Box
            component="span"
            sx={{
              mt: 1,
              display: "inline-flex",
              borderRadius: 999,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
              color: "primary.main",
              px: 1,
              py: 0.25,
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            {ROLE_LABELS[user.dashboard_role]}
          </Box>
        </Box>

        {/* Availability — sets the live status the server uses to gate
            notifications (DND silences the popup/snackbar; the bell still
            fills) and that drives presence (who's online). Mirrors the
            desktop tray's DND control; both write the one server-side state. */}
        <Box sx={{ px: 2, pt: 1.25, pb: 0.25 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "text.disabled",
            }}
          >
            Availability
          </Typography>
        </Box>
        {PRESENCE_STATUSES.map((s) => (
          <MenuItem
            key={s}
            data-testid={`status-${s}`}
            selected={status === s}
            onClick={() => {
              setStatus(s);
              close();
            }}
            sx={{ fontSize: 14 }}
          >
            <Box component="span" sx={{ mr: 1.5, display: "inline-flex" }}>
              <PresenceDot state={s} />
            </Box>
            {PRESENCE_STATUS_LABELS[s]}
            {status === s && (
              <CheckIcon fontSize="small" sx={{ ml: "auto", color: "primary.main" }} />
            )}
          </MenuItem>
        ))}
        <Divider />

        <MenuItem
          onClick={() => {
            close();
            navigate("/profile");
          }}
          sx={{ fontSize: 14 }}
        >
          <PersonOutlineIcon fontSize="small" sx={{ mr: 1 }} />
          Your profile
        </MenuItem>
        {canSeeSystemAdmin && (
          <MenuItem
            onClick={() => {
              close();
              navigate("/admin");
            }}
            sx={{ fontSize: 14 }}
          >
            <SettingsOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            System admin
          </MenuItem>
        )}
        <MenuItem onClick={onSignOut} sx={{ fontSize: 14, color: "error.main" }}>
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}

